class CombineSave {
  constructor(element) {
    this.element = element;

    this.moneyFormat =
      this.element.dataset.moneyFormat || '${{amount}}';

    this.buttonLabel =
      this.element.dataset.buttonLabel || 'ADD';

    this.itemLabel =
      this.element.dataset.itemLabel || 'ITEM';

    this.itemsLabel =
      this.element.dataset.itemsLabel || 'ITEMS';

    this.rows = [
      ...this.element.querySelectorAll('[data-combine-product]')
    ];

    this.button =
      this.element.querySelector('[data-combine-add]');

    this.buttonText =
      this.element.querySelector('[data-button-text]');

    this.savingWrapper =
      this.element.querySelector('[data-saving-wrapper]');

    this.savingAmount =
      this.element.querySelector('[data-saving-amount]');

    this.status =
      this.element.querySelector('[data-combine-status]');

    this.init();
  }

  init() {
    this.rows.forEach((row) => {
      const checkbox =
        row.querySelector('[data-product-checkbox]');

      const select =
        row.querySelector('[data-variant-select]');

      checkbox?.addEventListener('change', () => {
        this.updateTotals();
      });

      select?.addEventListener('change', () => {
        this.updateRow(row);
        this.updateTotals();
      });

      this.updateRow(row);
    });

    this.button?.addEventListener('click', () => {
      this.addSelectedItems();
    });

    this.updateTotals();
  }

  getVariants(row) {
    const script =
      row.querySelector('[data-product-variants]');

    if (!script) return [];

    try {
      return JSON.parse(script.textContent);
    } catch (error) {
      console.error('Combine & Save variants JSON error:', error);
      return [];
    }
  }

  getVariantId(row) {
    const select =
      row.querySelector('[data-variant-select]');

    const input =
      row.querySelector('[data-variant-input]');

    return Number(select?.value || input?.value);
  }

  getSelectedVariant(row) {
    const variantId = this.getVariantId(row);

    return this.getVariants(row).find(
      (variant) => Number(variant.id) === variantId
    );
  }

  updateRow(row) {
    const variant = this.getSelectedVariant(row);

    if (!variant) return;

    row.querySelectorAll('.combine-save__media, .combine-save__title').forEach((link) => {
      const url = new URL(link.href);
      url.searchParams.set('variant', variant.id);
      link.href = url.toString();
    });

    const select = row.querySelector('[data-variant-select]');
    const selectedOption = select?.selectedOptions[0];
    const image = row.querySelector('.combine-save__image');
    const imageUrl = selectedOption?.dataset.imageUrl || row.dataset.defaultImageUrl;

    if (image && selectedOption && imageUrl) {
      image.removeAttribute('srcset');
      image.removeAttribute('sizes');
      image.src = imageUrl;
      image.alt = selectedOption.dataset.imageUrl
        ? selectedOption.dataset.imageAlt
        : row.dataset.defaultImageAlt;
    }

    const currentPrice =
      row.querySelector('[data-current-price]');

    const comparePrice =
      row.querySelector('[data-compare-price]');

    if (currentPrice) {
      currentPrice.textContent =
        this.formatMoney(variant.price);
    }

    const hasDiscount =
      variant.compare_at_price &&
      variant.compare_at_price > variant.price;

    if (comparePrice) {
      if (hasDiscount) {
        comparePrice.textContent =
          this.formatMoney(variant.compare_at_price);

        comparePrice.hidden = false;
      } else {
        comparePrice.textContent = '';
        comparePrice.hidden = true;
      }
    }
  }

  getSelectedRows() {
    return this.rows.filter((row) => {
      const checkbox =
        row.querySelector('[data-product-checkbox]');

      return checkbox?.checked && !checkbox.disabled;
    });
  }

  updateTotals() {
    const selectedRows = this.getSelectedRows();

    let total = 0;
    let saving = 0;

    selectedRows.forEach((row) => {
      const variant = this.getSelectedVariant(row);

      if (!variant) return;

      total += Number(variant.price || 0);

      if (
        variant.compare_at_price &&
        variant.compare_at_price > variant.price
      ) {
        saving +=
          Number(variant.compare_at_price) -
          Number(variant.price);
      }
    });

    const count = selectedRows.length;

    if (this.button) {
      this.button.disabled = count === 0;
    }

    if (this.buttonText) {
      if (count === 0) {
        this.buttonText.textContent =
          `${this.buttonLabel} 0 ${this.itemsLabel}`;
      } else {
        const itemText =
          count === 1
            ? this.itemLabel
            : this.itemsLabel;

        this.buttonText.textContent =
          `${this.buttonLabel} ${count} ${itemText} — ${this.formatMoney(total)}`;
      }
    }

    if (this.savingWrapper && this.savingAmount) {
      if (saving > 0) {
        this.savingAmount.textContent =
          this.formatMoney(saving);

        this.savingWrapper.hidden = false;
      } else {
        this.savingAmount.textContent = '';
        this.savingWrapper.hidden = true;
      }
    }
  }

  async addSelectedItems() {
    const selectedRows = this.getSelectedRows();

    if (!selectedRows.length) return;

    const items = selectedRows
        .map((row) => {
        const variant = this.getSelectedVariant(row);

        if (!variant || !variant.available) {
            return null;
        }

        return {
            id: Number(variant.id),
            quantity: 1
        };
        })
        .filter(Boolean);

    if (!items.length) return;

    this.setLoading(true);
    this.setStatus('');

    try {
        const response = await fetch(
        window.theme?.routes?.cart_add_url || '/cart/add.js',
        {
            method: 'POST',
            headers: {
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
            'Accept': 'application/json'
            },
            body: JSON.stringify({
            items: items
            })
        }
        );

        const data = await response.json();

        if (!response.ok || data.status) {
          throw new Error(
              data.description ||
              data.message ||
              'Unable to add products to cart.'
          );
        }

        const freeShippingProgressBar = document.querySelector("#cart-drawer cart-progress-bar");

        if (freeShippingProgressBar) {
          const giftVariantId = freeShippingProgressBar.getAttribute("data-gift-variant-id");
          const giftThreshold = +freeShippingProgressBar.getAttribute("data-gift-threshold");

          if (giftThreshold && giftVariantId) {
            await freeShippingProgressBar.verifyGiftItem();
          }
        }

        this.setStatus('');

        const cartDrawer = document.querySelector(
        'cart-items[data-cart-drawer]'
        );

        if (cartDrawer) {
        document.dispatchEvent(
            new CustomEvent('theme:cart:refresh', {
            bubbles: true
            })
        );
        } else {
        window.location =
            window.theme?.routes?.cart_url || '/cart';
        }

    } catch (error) {
        console.error('Combine & Save:', error);

        this.setStatus(
        error.message || 'Something went wrong.'
        );
    } finally {
        this.setLoading(false);
        this.updateTotals();
    }
    }

  setLoading(state) {
    if (!this.button) return;

    this.button.classList.toggle(
      'is-loading',
      state
    );

    this.button.disabled =
      state || this.getSelectedRows().length === 0;

    this.button.setAttribute(
      'aria-busy',
      state ? 'true' : 'false'
    );
  }

  setStatus(message) {
    if (!this.status) return;

    this.status.textContent = message;
  }

  formatMoney(cents) {
    if (typeof cents === 'string') {
      cents = cents.replace('.', '');
    }

    const format =
      this.moneyFormat || '${{amount}}';

    const placeholderRegex =
      /\{\{\s*(\w+)\s*\}\}/;

    const match = format.match(
      placeholderRegex
    );

    if (!match) {
      return (
        Number(cents || 0) / 100
      ).toFixed(2);
    }

    const formatType = match[1];

    let value = '';

    switch (formatType) {
      case 'amount':
        value = this.formatWithDelimiters(
          cents,
          2,
          ',',
          '.'
        );
        break;

      case 'amount_no_decimals':
        value = this.formatWithDelimiters(
          cents,
          0,
          ',',
          '.'
        );
        break;

      case 'amount_with_comma_separator':
        value = this.formatWithDelimiters(
          cents,
          2,
          '.',
          ','
        );
        break;

      case 'amount_no_decimals_with_comma_separator':
        value = this.formatWithDelimiters(
          cents,
          0,
          '.',
          ','
        );
        break;

      case 'amount_with_apostrophe_separator':
        value = this.formatWithDelimiters(
          cents,
          2,
          "'",
          '.'
        );
        break;

      default:
        value = this.formatWithDelimiters(
          cents,
          2,
          ',',
          '.'
        );
    }

    return format.replace(
      placeholderRegex,
      value
    );
  }

  formatWithDelimiters(
    cents,
    precision = 2,
    thousands = ',',
    decimal = '.'
  ) {
    const number =
      Number(cents || 0) / 100;

    if (Number.isNaN(number)) {
      return '0';
    }

    const parts =
      number
        .toFixed(precision)
        .split('.');

    const dollars =
      parts[0].replace(
        /(\d)(?=(\d{3})+(?!\d))/g,
        `$1${thousands}`
      );

    return parts[1]
      ? dollars + decimal + parts[1]
      : dollars;
  }
}

const initCombineSave = () => {
  document
    .querySelectorAll(
      '[data-combine-save]:not([data-initialized])'
    )
    .forEach((element) => {
      element.dataset.initialized = 'true';

      new CombineSave(element);
    });
};

document.addEventListener(
  'DOMContentLoaded',
  initCombineSave
);

document.addEventListener(
  'shopify:section:load',
  initCombineSave
);
